import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  Link,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  History as StaleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getDataQualityMetrics } from '../../../api/utilityServices';

interface DataQualityOverviewProps {
  isLoading: boolean;
}

const formatFieldName = (field: string) =>
  field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase());

const DataQualityOverview: React.FC<DataQualityOverviewProps> = ({ isLoading }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getDataQualityMetrics();
        setData(result);
      } catch (err) {
        setError('Failed to fetch data quality metrics');
        console.error('Error fetching data quality metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      fetchData();
    }
  }, [isLoading]);

  const goToClient = (clientId: string) => navigate(`/utilities/clients/${clientId}`);
  const goToContract = (serviceId: string) => navigate(`/utilities/contracts/${serviceId}`);

  const healthColor = (pct: number) =>
    pct >= 90 ? 'success.main' : pct >= 70 ? 'warning.main' : 'error.main';

  const severityIcon = (affected: number) => {
    if (affected === 0) return <CheckCircleIcon color="success" />;
    if (affected < 10) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  const RecordLink: React.FC<{ id: string; kind: 'client' | 'service'; children: React.ReactNode }> = ({ id, kind, children }) => (
    <Link
      component="button"
      variant="body2"
      onClick={() => (kind === 'client' ? goToClient(id) : goToContract(id))}
      sx={{
        color: '#fff',
        textDecoration: 'underline',
        '&:hover': { textDecoration: 'none' }
      }}
    >
      {children}
    </Link>
  );

  const MissingChips: React.FC<{ missing: Record<string, boolean> }> = ({ missing }) => (
    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {Object.entries(missing)
        .filter(([, isMissing]) => isMissing)
        .map(([field]) => (
          <Chip key={field} label={formatFieldName(field)} size="small" color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
        ))}
    </Box>
  );

  // Scrollable list — shows every flagged record rather than truncating at 3.
  const IssueList: React.FC<{ items: any[]; renderItem: (item: any, index: number) => React.ReactNode }> = ({ items, renderItem }) => (
    <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'success.main' }}>None — all clear.</Typography>
      ) : (
        items.map(renderItem)
      )}
    </Box>
  );

  const IssueGroup: React.FC<{ title: string; hint: string; count: number; items: any[]; renderItem: (item: any, index: number) => React.ReactNode }> = ({
    title, hint, count, items, renderItem
  }) => (
    <Grid item xs={12} md={4}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: '#fff' }}>{title}</Typography>
        <Chip label={count} size="small" color={count === 0 ? 'success' : 'error'} sx={{ height: 20 }} />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {hint}
      </Typography>
      <IssueList items={items} renderItem={renderItem} />
    </Grid>
  );

  const Section: React.FC<{
    title: string;
    subtitle: string;
    affected: number;
    checked: number;
    defaultExpanded?: boolean;
    children: React.ReactNode;
  }> = ({ title, subtitle, affected, checked, defaultExpanded, children }) => (
    <Accordion defaultExpanded={defaultExpanded} sx={{ backgroundColor: 'rgba(255,255,255,0.05)', mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
          {severityIcon(affected)}
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Chip
              label={affected === 0 ? 'All clear' : `${affected} of ${checked} records`}
              color={affected === 0 ? 'success' : affected < 10 ? 'warning' : 'error'}
              size="small"
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>{children}</Grid>
      </AccordionDetails>
    </Accordion>
  );

  if (loading || isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Data Quality Overview</Typography>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading data quality metrics...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Data Quality Overview</Typography>
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {error || 'No data available'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const { overall, clientDataCompleteness, contractDataIntegrity, serviceConfigurationIssues, commissionTrackingQuality, dataFreshnessIndicators } = data;
  const staleTotal = dataFreshnessIndicators.summary.staleClients + dataFreshnessIndicators.summary.outdatedContracts;

  const clientIssueRow = (issue: any, index: number) => (
    <Box key={index} sx={{ mb: 1.5 }}>
      <RecordLink id={issue.clientId} kind="client">{issue.companyName}</RecordLink>
      {issue.missing && <MissingChips missing={issue.missing} />}
    </Box>
  );

  const serviceIssueRow = (issue: any, index: number) => (
    <Box key={index} sx={{ mb: 1.5 }}>
      <RecordLink id={issue.serviceId} kind="service">
        {issue.clientName}{issue.serviceType ? ` — ${issue.serviceType}` : ''}
      </RecordLink>
      {issue.status && (
        <Chip label={issue.status} size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 18, color: 'text.secondary' }} />
      )}
      {issue.missing && <MissingChips missing={issue.missing} />}
      {issue.uplift !== undefined && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          Uplift recorded: {issue.uplift}
        </Typography>
      )}
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Data Quality Overview
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Checks cover active clients and open contracts. Staleness is tracked separately below.
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Tooltip title="Share of checked records with no flagged issues">
                  <Typography variant="h4" sx={{ color: healthColor(overall.cleanPercent), fontWeight: 'bold' }}>
                    {overall.cleanPercent}%
                  </Typography>
                </Tooltip>
                <Typography variant="body2" sx={{ color: '#fff' }}>Data Health</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: overall.recordsWithIssues === 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                  {overall.recordsWithIssues}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>Need Attention</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {clientDataCompleteness.totalClients}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>Active Clients</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {serviceConfigurationIssues.totalServices}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>Contracts</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                  {staleTotal}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>Stale Records</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Client Data Completeness */}
        <Section
          title="Client Data Completeness"
          subtitle="Active clients missing contact or business information"
          affected={clientDataCompleteness.affectedRecords}
          checked={clientDataCompleteness.totalClients}
          defaultExpanded
        >
          <IssueGroup
            title="Missing Contact Info"
            hint="No email, phone number, or trading address on file."
            count={clientDataCompleteness.summary.missingContactInfo}
            items={clientDataCompleteness.issues.missingContactInfo}
            renderItem={clientIssueRow}
          />
          <IssueGroup
            title="Incomplete Business Details"
            hint="Business type, industry, or registered address missing (registered address required for limited companies)."
            count={clientDataCompleteness.summary.incompleteBusinessDetails}
            items={clientDataCompleteness.issues.incompleteBusinessDetails}
            renderItem={clientIssueRow}
          />
          <IssueGroup
            title="No Active Contact"
            hint="Client has no active contact person — contacts are needed for LOAs and quotes."
            count={clientDataCompleteness.summary.contactPersonGaps}
            items={clientDataCompleteness.issues.contactPersonGaps}
            renderItem={clientIssueRow}
          />
        </Section>

        {/* Contract Data Integrity */}
        <Section
          title="Contract Data Integrity"
          subtitle="Open contracts missing dates, values, or supplier"
          affected={contractDataIntegrity.affectedRecords}
          checked={contractDataIntegrity.totalServices}
        >
          <IssueGroup
            title="Missing Contract Dates"
            hint="Open contract with no start or end date — end dates drive expiry and renewal forecasting."
            count={contractDataIntegrity.summary.missingContractDates}
            items={contractDataIntegrity.issues.missingContractDates}
            renderItem={serviceIssueRow}
          />
          <IssueGroup
            title="Incomplete Contract Values"
            hint="Monthly cost or uplift missing — commission can't be tracked accurately."
            count={contractDataIntegrity.summary.incompleteContractValues}
            items={contractDataIntegrity.issues.incompleteContractValues}
            renderItem={serviceIssueRow}
          />
          <IssueGroup
            title="Supplier Info Gaps"
            hint="Contract has no supplier recorded."
            count={contractDataIntegrity.summary.supplierInfoGaps}
            items={contractDataIntegrity.issues.supplierInfoGaps}
            renderItem={serviceIssueRow}
          />
        </Section>

        {/* Service Configuration */}
        <Section
          title="Service Configuration"
          subtitle="Orphaned, incomplete, or duplicated contract records"
          affected={serviceConfigurationIssues.affectedRecords}
          checked={serviceConfigurationIssues.totalServices}
        >
          <IssueGroup
            title="Orphaned Services"
            hint="Contract not linked to any client record."
            count={serviceConfigurationIssues.summary.orphanedServices}
            items={serviceConfigurationIssues.issues.orphanedServices}
            renderItem={serviceIssueRow}
          />
          <IssueGroup
            title="Missing Service Details"
            hint="Service type or supplier not recorded."
            count={serviceConfigurationIssues.summary.missingServiceDetails}
            items={serviceConfigurationIssues.issues.missingServiceDetails}
            renderItem={serviceIssueRow}
          />
          <IssueGroup
            title="Potential Duplicates"
            hint="Two or more open contracts for the same client, service type, and supplier — check these aren't double-keyed."
            count={serviceConfigurationIssues.summary.potentialDuplicates}
            items={serviceConfigurationIssues.issues.potentialDuplicates}
            renderItem={(group: any, index: number) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  {group.clientName} — {group.serviceType} / {group.supplier} ({group.duplicateCount} contracts)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {group.serviceIds.map((id: string) => (
                    <RecordLink key={id} id={id} kind="service">view</RecordLink>
                  ))}
                </Box>
              </Box>
            )}
          />
        </Section>

        {/* Commission Tracking */}
        <Section
          title="Commission Tracking"
          subtitle="Live contracts missing the data commissions are calculated from"
          affected={commissionTrackingQuality.affectedRecords}
          checked={commissionTrackingQuality.totalServices}
        >
          <IssueGroup
            title="Missing Monthly Cost"
            hint="No monthly cost recorded — commission receipts can't be projected."
            count={commissionTrackingQuality.summary.missingCommissionData}
            items={commissionTrackingQuality.issues.missingCommissionData}
            renderItem={serviceIssueRow}
          />
          <IssueGroup
            title="Unusual Uplift"
            hint="Uplift outside the expected 1–500 range — worth a sanity check."
            count={commissionTrackingQuality.summary.inconsistentCommissionStructures}
            items={commissionTrackingQuality.issues.inconsistentCommissionStructures}
            renderItem={serviceIssueRow}
          />
          <IssueGroup
            title="No Uplift Recorded"
            hint="Live contract with zero or missing uplift — confirm this is intentional."
            count={commissionTrackingQuality.summary.incompleteUpliftInfo}
            items={commissionTrackingQuality.issues.incompleteUpliftInfo}
            renderItem={serviceIssueRow}
          />
        </Section>

        {/* Data Freshness */}
        <Accordion sx={{ backgroundColor: 'rgba(255,255,255,0.05)', mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
              <StaleIcon color="info" />
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>Data Freshness</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Records that haven't been touched in a while — may need a review, not necessarily a fix. Not counted in the health score.
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Chip label={`${staleTotal} stale`} color="info" size="small" />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                  Clients untouched 3+ months: {dataFreshnessIndicators.summary.staleClients}
                </Typography>
                <IssueList
                  items={dataFreshnessIndicators.staleClients}
                  renderItem={(client: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <RecordLink id={client.clientId} kind="client">{client.companyName}</RecordLink>
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                        (last updated {client.daysSinceUpdate} days ago)
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                  Contracts untouched 6+ months: {dataFreshnessIndicators.summary.outdatedContracts}
                </Typography>
                <IssueList
                  items={dataFreshnessIndicators.outdatedContracts}
                  renderItem={(contract: any, index: number) => (
                    <Box key={index} sx={{ mb: 1 }}>
                      <RecordLink id={contract.serviceId} kind="service">
                        {contract.clientName} — {contract.serviceType}
                      </RecordLink>
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                        (last updated {contract.daysSinceUpdate} days ago)
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default DataQualityOverview;
